import {
  Items,
  JSONEncode,
  Keys,
  StringToUInt8,
  UInt8ToString,
} from "@pdfking/tfunc"
import { RenameMap } from "../maps/RenameMap"

export type WasmValType = "i32" | "i64" | "f32" | "f64"

export type WasmImportSig = {
  readonly module: string
  readonly minified: string
  readonly minifiedModule: string
  readonly params: readonly WasmValType[]
  readonly results: readonly WasmValType[]
}

export type RenameWasmResult = {
  readonly wasm: Uint8Array
  readonly imports: Readonly<Record<string, WasmImportSig>>
}

const VAL_TYPE: Readonly<Record<number, WasmValType>> = {
  127: "i32",
  126: "i64",
  125: "f32",
  124: "f64",
}

type FuncType = {
  readonly params: WasmValType[]
  readonly results: WasmValType[]
}

/** Minify import/export names in a WASM binary and extract import signatures. */
export function RenameWasm(
  buf: Uint8Array,
  rename = (name: string): string =>
    (RenameMap as Record<string, string>)[name] ?? name
): RenameWasmResult {
  const chunks: Uint8Array[] = []
  let pos = 0

  const readU8 = () => buf[pos++]!
  const readLeb128 = () => {
    let result = 0
    let shift = 0
    let byte: number
    do {
      byte = buf[pos++]!
      result |= (byte & 0x7f) << shift
      shift += 7
    } while (byte & 0x80)
    return result
  }
  const readBytes = (n: number) => buf.subarray(pos, (pos += n))
  const readStr = () => UInt8ToString(readBytes(readLeb128()))

  const encodeLeb128 = (value: number): Uint8Array => {
    const bytes: number[] = []
    do {
      let byte = value & 0x7f
      value >>>= 7
      if (value) byte |= 0x80
      bytes.push(byte)
    } while (value)
    return new Uint8Array(bytes)
  }

  const encodeStr = (s: string): Uint8Array => {
    const b = StringToUInt8(s)
    const len = encodeLeb128(b.length)
    const out = new Uint8Array(len.length + b.length)
    out.set(len)
    out.set(b, len.length)
    return out
  }

  const push = (...parts: (Uint8Array | number[])[]) => {
    for (const p of parts)
      chunks.push(p instanceof Uint8Array ? p : new Uint8Array(p))
  }

  // Parsed type section: array of function type signatures
  const types: FuncType[] = []
  // Collected import function signatures keyed by original field name
  const imports: Record<string, WasmImportSig> = {}

  // Magic + version
  push(readBytes(8))

  while (pos < buf.length) {
    const sectionId = readU8()
    const sectionLen = readLeb128()

    if (sectionId === 1) {
      // Type section — parse func types, copy as-is
      const sectionStart = pos
      const count = readLeb128()
      for (let i = 0; i < count; i++) {
        const form = readU8() // 0x60 = func type
        if (form !== 0x60) {
          pos = sectionStart + sectionLen
          break
        }
        const paramCount = readLeb128()
        const params: WasmValType[] = []
        for (let p = 0; p < paramCount; p++)
          params.push(VAL_TYPE[readU8()] ?? "i32")
        const resultCount = readLeb128()
        const results: WasmValType[] = []
        for (let r = 0; r < resultCount; r++)
          results.push(VAL_TYPE[readU8()] ?? "i32")
        types.push({ params, results })
      }
      // Re-read and copy the raw bytes
      pos = sectionStart
      push([sectionId], encodeLeb128(sectionLen), readBytes(sectionLen))
    } else if (sectionId === 2) {
      // Import section
      const body: (Uint8Array | number[])[] = []
      const count = readLeb128()
      body.push(encodeLeb128(count))
      for (let i = 0; i < count; i++) {
        const mod = readStr()
        const field = readStr()
        const kind = readU8()
        const renamedMod = rename(mod)
        const renamedField = rename(field)
        body.push(encodeStr(renamedMod))
        body.push(encodeStr(renamedField))
        body.push([kind])
        if (kind === 0) {
          const start = pos
          const typeIdx = readLeb128()
          body.push(buf.slice(start, pos))
          const ft = types[typeIdx]
          if (ft) {
            imports[field] = {
              module: mod,
              minified: renamedField,
              minifiedModule: renamedMod,
              params: ft.params,
              results: ft.results,
            }
          }
        } else if (kind === 1) {
          body.push([readU8()])
          const flags = readU8()
          body.push([flags])
          const start = pos
          readLeb128()
          if (flags & 1) readLeb128()
          body.push(buf.slice(start, pos))
        } else if (kind === 2) {
          const flags = readU8()
          body.push([flags])
          const start = pos
          readLeb128()
          if (flags & 1) readLeb128()
          body.push(buf.slice(start, pos))
        } else if (kind === 3) {
          body.push([readU8(), readU8()])
        }
      }
      const bodyLen = body.reduce((s, c) => s + c.length, 0)
      push([sectionId], encodeLeb128(bodyLen), ...body)
    } else if (sectionId === 7) {
      // Export section
      const body: (Uint8Array | number[])[] = []
      const count = readLeb128()
      body.push(encodeLeb128(count))
      for (let i = 0; i < count; i++) {
        const name = readStr()
        const kind = readU8()
        const start = pos
        readLeb128()
        body.push(encodeStr(rename(name)))
        body.push([kind])
        body.push(buf.slice(start, pos))
      }
      const bodyLen = body.reduce((s, c) => s + c.length, 0)
      push([sectionId], encodeLeb128(bodyLen), ...body)
    } else {
      // Copy section as-is
      push([sectionId], encodeLeb128(sectionLen), readBytes(sectionLen))
    }
  }

  const totalLen = chunks.reduce((s, c) => s + c.length, 0)
  const wasm = new Uint8Array(totalLen)
  let offset = 0
  for (const chunk of chunks) {
    wasm.set(chunk, offset)
    offset += chunk.length
  }

  return { wasm, imports }
}

const TS_TYPE: Readonly<Record<WasmValType, string>> = {
  i32: "number",
  i64: "bigint",
  f32: "number",
  f64: "number",
}

/** Generate a TypeScript source with import signatures keyed by DefaultMinimap. */
export const FormatSignaturesTS = (
  allImports: Readonly<Record<string, Readonly<Record<string, WasmImportSig>>>>
): string => {
  const minimapNames = new Set(Keys(RenameMap))
  const toExportName = (v: string) => (v.endsWith("64") ? "wasm64" : "wasm32")

  const lines = [
    "/* eslint-disable */",
    "// biome-ignore-all lint: auto-generated",
    "// biome-ignore-all format: auto-generated",
    "//",
    "// Auto-generated by build script. Do not edit manually.",
    "//",
    "",
    'import type { DefaultMinimap as M, WasmImports } from "@pdfking/library-core"',
    "",
  ]
  for (const [variant, sigs] of Items(allImports)) {
    const id = toExportName(variant)
    const T = id === "wasm64" ? "bigint" : "number"
    lines.push(`export type ${id} = {`)
    for (const [name, sig] of Items(sigs)) {
      const params = sig.params.map((t, i) => `a${i}: ${TS_TYPE[t]}`).join(", ")
      const ret =
        sig.results.length === 0
          ? "void"
          : sig.results.map(t => TS_TYPE[t]).join(" | ")
      const key = minimapNames.has(name)
        ? `[M.${name}]`
        : `[${JSONEncode(name)}]`
      lines.push(`  ${key}: (${params}) => ${ret}`)
    }
    lines.push("}", "")
    lines.push(`null! as ${id} satisfies Partial<WasmImports<${T}>>`, "")
  }
  return `${lines.join("\n")}\n`
}
